'use client';

import { ProductAnalysisProblemEntity } from '@/entities/product-analysis.entity';
import { getProblemTypeTitle } from '@/constants/problem-type-titles';
import { formatDateTimePartsInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import Modal from '@/ui/Modal';
import { ChangeEvent, FC } from 'react';
import ProductAnalysisRadarChart from '@/components/ProductAnalysisRadarChart';
import ProductAnalysisReviewsTimelineChart from '@/components/ProductAnalysisReviewsTimelineChart';
import Rating from '@/ui/Rating';
import Button from '@/ui/Button';
import { UIPriority } from '@/enums/ui-priorities';
import { ProductAnalysisModalControllerBindings } from '@/components/product-analysis-modal/use-product-analysis-modal-controller';

type ProductAnalysisModalViewProps = ProductAnalysisModalControllerBindings & {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    loading: boolean;
    error: string | null;
    productName: string;
};

const renderValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    return value;
};

const formatIntensity = (value: number | null) => {
    if (value === null) {
        return '—';
    }

    return value.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

const formatDate = (value: string) => {
    const dateTime = formatDateTimePartsInGmtPlus3(value);
    if (!dateTime) {
        return renderValue(value);
    }

    return (
        <div className="flex flex-col leading-tight">
            <span className="text-[80%]">{dateTime.date}</span>
            <span>{dateTime.time}</span>
        </div>
    );
};

const formatDateTimeLabel = (timestamp: number | null): string => {
    if (timestamp === null) {
        return '—';
    }

    const dateTime = formatDateTimePartsInGmtPlus3(new Date(timestamp));
    if (!dateTime) {
        return '—';
    }

    return `${dateTime.date} ${dateTime.time}`;
};

type ProblemsTableProps = {
    title: string;
    tone: 'negative' | 'positive' | 'neutral';
    rows: ProductAnalysisProblemEntity[];
};

const ProblemsTable: FC<ProblemsTableProps> = ({ title, tone, rows }) => {
    const titleClassName =
        tone === 'negative'
            ? 'border-rose-200 bg-rose-50 text-rose-700'
            : tone === 'positive'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-slate-200 bg-slate-100 text-slate-700';

    return (
        <section className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${titleClassName}`}>{rows.length}</span>
            </div>
            {rows.length ? (
                <div className="mt-3 overflow-auto">
                    <table className="w-full min-w-[760px] table-auto text-xs text-slate-700">
                        <thead>
                            <tr className="border-y border-slate-200 bg-slate-50">
                                <th className="px-2 py-1 text-left font-medium">Дата</th>
                                <th className="px-2 py-1 text-left font-medium">Оценка</th>
                                <th className="px-2 py-1 text-left font-medium">Интенсивность</th>
                                <th className="px-2 py-1 text-left font-medium">Описание</th>
                                <th className="px-2 py-1 text-left font-medium">Тип</th>
                                <th className="px-2 py-1 text-left font-medium">Уверенность</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr
                                    key={`${row.reviewId}-${row.createdDate}-${index}`}
                                    className="border-b border-slate-100 align-top"
                                >
                                    <td className="px-2 py-1.5">{formatDate(row.createdDate)}</td>
                                    <td className="px-2 py-1.5">
                                        <Rating value={row.rating} size={12} />
                                    </td>
                                    <td className="px-2 py-1.5">{formatIntensity(row.intensity)}</td>
                                    <td className="max-w-[320px] px-2 py-1.5 [overflow-wrap:anywhere]">
                                        {renderValue(row.modelDesc)}
                                    </td>
                                    <td className="max-w-[200px] px-2 py-1.5 [overflow-wrap:anywhere]">
                                        {renderValue(getProblemTypeTitle(row.problem))}
                                    </td>
                                    <td className="px-2 py-1.5">{renderValue(row.confidence)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="mt-3 rounded border border-dashed border-slate-300 p-2 text-xs text-slate-500">
                    Нет записей для выбранного сентимента.
                </p>
            )}
        </section>
    );
};

export const ProductAnalysisModalView: FC<ProductAnalysisModalViewProps> = ({
    isShown,
    setIsShown,
    loading,
    error,
    productName,
    isExportLoading,
    timelineBounds,
    totalWindowDays,
    normalizedWindowWidthDays,
    maxWindowStartOffsetDays,
    normalizedWindowStartOffsetDays,
    maxTimelineAggregationWindowDays,
    normalizedTimelineAggregationWindowDays,
    selectedTimeWindow,
    selectedWindowEndTimestamp,
    reviewsTimelinePoints,
    filteredAnalysis,
    setWindowWidthDays,
    setWindowStartOffsetDays,
    setTimelineAggregationWindowDays,
    exportPdfReport,
}) => {
    const onWindowWidthChange = (event: ChangeEvent<HTMLInputElement>) => {
        setWindowWidthDays(Number(event.target.value));
    };

    const onWindowStartChange = (event: ChangeEvent<HTMLInputElement>) => {
        setWindowStartOffsetDays(Number(event.target.value));
    };

    const onTimelineAggregationWindowChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTimelineAggregationWindowDays(Number(event.target.value));
    };

    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title={`Анализ: ${productName}`}
            closable
            className="h-[92vh] w-[96vw] max-w-[1240px]"
        >
            <div className="h-full w-full min-h-0 overflow-auto p-4">
                {!loading && !error && filteredAnalysis && (
                    <div className="mb-3 flex justify-end">
                        <Button
                            className="w-auto px-3"
                            priorityType={UIPriority.SECONDARY}
                            uiSize="compact"
                            onClick={() => {
                                void exportPdfReport();
                            }}
                            disabled={isExportLoading}
                        >
                            {isExportLoading ? 'Экспорт...' : 'Экспорт PDF'}
                        </Button>
                    </div>
                )}
                {loading && (
                    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600">
                        Загрузка анализа...
                    </div>
                )}
                {!loading && error && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                        {error}
                    </div>
                )}
                {!loading && !error && filteredAnalysis && (
                    <div className="flex flex-col gap-4 text-sm text-slate-800">
                        <section className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <h3 className="text-sm font-semibold text-slate-800">Описание</h3>
                            <p className="mt-1 whitespace-pre-line break-words">{filteredAnalysis.description}</p>
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                                <span>Отзывов с анализом: {filteredAnalysis.analyzedReviewsCount}</span>
                                <span>Всего выделенных проблем: {filteredAnalysis.totalProblemsCount}</span>
                            </div>
                        </section>

                        <ProductAnalysisRadarChart points={filteredAnalysis.chartPoints} />
                        {timelineBounds && selectedTimeWindow && (
                            <section className="rounded-lg border border-slate-200 bg-white p-3">
                                <h3 className="text-sm font-semibold text-slate-800">Фильтр по времени</h3>
                                <p className="mt-1 text-xs text-slate-500">
                                    График, таблицы, счетчики и PDF учитывают выбранное временное окно.
                                </p>
                                <div className="mt-3 grid gap-4 md:grid-cols-2">
                                    <label className="flex flex-col gap-1">
                                        <span className="text-xs font-medium text-slate-700">
                                            Ширина окна: {normalizedWindowWidthDays} дн.
                                        </span>
                                        <input
                                            type="range"
                                            min={1}
                                            max={totalWindowDays}
                                            step={1}
                                            value={normalizedWindowWidthDays}
                                            onChange={onWindowWidthChange}
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1">
                                        <span className="text-xs font-medium text-slate-700">
                                            Начало окна: +{normalizedWindowStartOffsetDays} дн.
                                        </span>
                                        <input
                                            type="range"
                                            min={0}
                                            max={maxWindowStartOffsetDays}
                                            step={1}
                                            value={normalizedWindowStartOffsetDays}
                                            onChange={onWindowStartChange}
                                            disabled={maxWindowStartOffsetDays === 0}
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1 md:col-span-2">
                                        <span className="text-xs font-medium text-slate-700">
                                            Агрегация линейного графика: {normalizedTimelineAggregationWindowDays} дн.
                                        </span>
                                        <input
                                            type="range"
                                            min={1}
                                            max={maxTimelineAggregationWindowDays}
                                            step={1}
                                            value={normalizedTimelineAggregationWindowDays}
                                            onChange={onTimelineAggregationWindowChange}
                                        />
                                    </label>
                                </div>
                                <p className="mt-2 text-xs text-slate-600">
                                    Период: {formatDateTimeLabel(selectedTimeWindow.startTimestamp)} —{' '}
                                    {formatDateTimeLabel(selectedWindowEndTimestamp)}
                                </p>
                            </section>
                        )}
                        <ProductAnalysisReviewsTimelineChart
                            points={reviewsTimelinePoints}
                            aggregationWindowDays={normalizedTimelineAggregationWindowDays}
                        />

                        <ProblemsTable
                            title="С негативным сентиментом"
                            tone="negative"
                            rows={filteredAnalysis.negativeProblems}
                        />
                        <ProblemsTable
                            title="С нейтральным сентиментом"
                            tone="neutral"
                            rows={filteredAnalysis.neutralProblems || []}
                        />
                        <ProblemsTable
                            title="С позитивным сентиментом"
                            tone="positive"
                            rows={filteredAnalysis.positiveProblems}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};
